/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'
import '@fil-global/gds-webcomponents/fil-icon/fil-icon.component'
import '@fil-global/gds-webcomponents/fil-pagination/fil-pagination.component'
import { detectIEEdge } from '@fil-global/gds-components/scripts/util/util'
import { useIntl } from 'react-intl'
import { Checkbox, Button } from 'gdpp-components/dist/cjs/components/atoms'

import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import ExternalLink from '../../atoms/ExternalLink'
import Date from '../../atoms/Date'
import Number from '../../atoms/Number'
import Dropdown from '../../atoms/Dropdown'
import Select from '../../atoms/Select'
import Loader from '../../atoms/Loader'
import Switch from '../../atoms/Switch'
import ContentLabel from '../ContentLabel'
import Pagination from '../Pagination'

import './style.scss'

const Table = ({
  tableId,
  isSelectable,
  tableData: defaultTableData,
  caption,
  title,
  size,
  textSize,
  necessaryElements,
  necessaryFunctions,
  extraClasses,
  needInitSort,
  isSimpleTable,
  hasMobileSort,
  withoutDataPlaceholder,
  defaultSortingValue,
  tableDisclaimer,
  scrollable,
  sticky,
  withOneKeyExpand,
  callback,
  ...extraProps
}) => {
  const mobileSortSwitchContent = [
    {
      id: 'desc',
      name: 'fil-table-mobile-sort-toggle',
      value: 'desc',
      icon: 'custom-arrow-down'
    },
    {
      id: 'asc',
      name: 'fil-table-mobile-sort-toggle',
      value: 'asc',
      icon: 'custom-arrow-up'
    }
  ]

  const intl = useIntl()
  const browser = detectIEEdge()
  const isIEbrowser = browser && browser === 11

  const deepClone = (preData) => JSON.parse(JSON.stringify(preData))
  const getTopSelectorStatus = (targetTableData) => {
    const allRows = _(targetTableData.body)
      .map((group) => (group.rows ? group.rows : group))
      .flattenDeep()
      .value()

    const unselectedRows = _(allRows)
      .filter((row) => !row.selected)
      .value()

    let returnedStatus = 'none'

    if (unselectedRows.length === allRows.length) {
      returnedStatus = 'none'
    } else if (unselectedRows.length !== allRows.length && unselectedRows.length > 0) {
      returnedStatus = 'indeterminate'
    } else if (!unselectedRows.length) {
      returnedStatus = 'all'
    }

    return returnedStatus
  }

  const [selectStatus, setSelectStatus] = useState(false)
  const [tableData, setTableData] = useState()
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsCount, setItemsCount] = useState()
  const [sortIndex, setSortIndex] = useState()
  const [sortByStatus, setSortByStatus] = useState()
  const [mobileSortOpt, setMobileSortOpt] = useState()
  const [mobileSortOptionsConf, setMobileSortOptionsConf] = useState()
  const [isScrollable, setScrollableStatus] = useState(scrollable)
  const [hasExpandedArrow, setHasExpandedArrow] = useState(false)
  const [isExpandedAllGroup, expandAllGroup] = useState(false)

  const sortByStatusRef = useRef(sortByStatus)
  const setSortByStatusRef = (x) => {
    sortByStatusRef.current = x
    setSortByStatus(x)
  }

  const splitData = (originData = tableData.body, newCurrentPage = currentPage, newItemsCount = itemsCount) => {
    const startIndex = (newCurrentPage - 1) * newItemsCount
    const endIndex = startIndex + newItemsCount
    return _.slice(originData, startIndex, endIndex)
  }

  const commonSortHandler = ({ sortedData, index, type, sortedBy, sortedItem, isDefaultSort, firstSort, keepCurrentSortStatus }) => {
    const tempBodyData = deepClone(sortedData.body)
    const tempHeaderData = deepClone(sortedData.headers)
    const getSortedStatus = () => {
      return isDefaultSort ? sortedBy : sortedBy ? (sortedBy === 'asc' ? 'desc' : 'asc') : type.toLowerCase() === 'number' ? 'desc' : 'asc'
    }

    const currentSortedStatus = firstSort || keepCurrentSortStatus ? sortedBy : getSortedStatus()

    setSortIndex(index)
    setSortByStatusRef(currentSortedStatus)
    setMobileSortOpt(tempHeaderData[index].value)

    const defaultSortedItem = sortedItem || 'value'

    const formattedHeader = isDefaultSort
      ? tempHeaderData
      : tempHeaderData.map((header, headerIndex) => {
          return {
            ...header,
            sortedBy: headerIndex === index ? currentSortedStatus : ''
          }
        })

    const sortHandler = (a, b) => {
      const aCell = Array.isArray(a.cells[index]) ? a.cells[index][0] : a.cells[index]
      const bCell = Array.isArray(b.cells[index]) ? b.cells[index][0] : b.cells[index]
      const aValue =
        type.toLowerCase() === 'string'
          ? aCell[defaultSortedItem].toLowerCase()
          : _.isNaN(parseFloat(aCell[defaultSortedItem]))
            ? defaultSortingValue
            : parseFloat(aCell[defaultSortedItem])
      const bValue =
        type.toLowerCase() === 'string'
          ? bCell[defaultSortedItem].toLowerCase()
          : _.isNaN(parseFloat(bCell[defaultSortedItem]))
            ? defaultSortingValue
            : parseFloat(bCell[defaultSortedItem])

      let result = null
      if (type.toLowerCase() === 'number') {
        result = currentSortedStatus === 'asc' ? aValue - bValue : bValue - aValue
      } else {
        result = currentSortedStatus === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }

      return result
    }

    let formattedBody = []

    if (!_.isEmpty(tempBodyData) && tempBodyData[0].parentNode) {
      formattedBody = tempBodyData.map((group) => ({
        ...group,
        rows: group.rows.sort(sortHandler)
      }))
    } else {
      const allRows = tempBodyData.map((item) => (item.rows ? item.rows : item))
      formattedBody = allRows.sort(sortHandler)
    }

    if (!firstSort && necessaryFunctions.trackSorting) {
      necessaryFunctions.trackSorting({
        sortIndex: index,
        sortByStatus: currentSortedStatus
      })
    }

    callback(formattedBody)

    return {
      ...sortedData,
      headers: formattedHeader,
      body: formattedBody
    }
  }

  const handleRowSelection = (groupIndex, rowIndex, status) => {
    const tempTableData = JSON.parse(JSON.stringify(tableData))
    if (!_.isUndefined(groupIndex)) {
      tempTableData.body[groupIndex].rows[rowIndex].selected = status
    } else {
      tempTableData.body[rowIndex].selected = status
    }
    const topSelectorStatus = getTopSelectorStatus(tempTableData)

    setSelectStatus(topSelectorStatus)
    setTableData(tempTableData)
  }

  const handleSelectAllSelection = (status) => {
    const tempTableData = deepClone(tableData)
    const allRows = _(tempTableData.body)
      .map((group) => ({
        ...group,
        rows: group.rows.map((row) => ({
          ...row,
          selected: status
        }))
      }))
      .value()

    if (status) {
      setSelectStatus('all')
    } else {
      setSelectStatus('none')
    }

    setTableData({
      ...tempTableData,
      ...{
        body: allRows
      }
    })
  }

  const handlePaginationChange = ({ currentPage: newCurrentPage, itemsCount: newItemsCount }) => {
    if (!_.isUndefined(newItemsCount)) {
      setItemsCount(newItemsCount)
    }

    if (!_.isUndefined(newCurrentPage)) {
      setCurrentPage(newCurrentPage)
    }
  }

  const getComponentTemplate = ({ item, itemIndex, cellIndex, row, rowIndex, groupIndex }) => {
    let template = null
    const { type, value, props = {}, ...itemProps } = item
    if (type === 'title') {
      template = (
        <span
          key={`table-${tableId}_title-${rowIndex}-${cellIndex}-${itemIndex}`}
          className={classnames('fil-table--cell-title', itemProps.extraClasses)}
        >
          {value}
        </span>
      )
    } else if (type === 'contentLabel') {
      const { translation, extraClasses: contentLabelClasses } = itemProps
      template = (
        <ContentLabel
          key={`table-${tableId}_contentLabel-${rowIndex}-${cellIndex}-${itemIndex}`}
          value={value}
          translation={translation}
          extraClasses={contentLabelClasses}
        />
      )
    } else if (type === 'label') {
      template = (
        <span key={`table-${tableId}_label-${rowIndex}-${cellIndex}-${itemIndex}`} className={itemProps.extraClasses}>
          {value}
        </span>
      )
    } else if (type === 'externalLink') {
      template = (
        <ExternalLink key={`table-${tableId}_externalLink-${rowIndex}-${cellIndex}-${itemIndex}`} value={value} {...props} {...itemProps} />
      )
    } else if (type === 'date') {
      template = <Date key={`table-${tableId}_date-${rowIndex}-${cellIndex}-${itemIndex}`} value={value} {...props} {...itemProps} />
    } else if (type === 'number') {
      template = <Number key={`table-${tableId}_number-${rowIndex}-${cellIndex}-${itemIndex}`} value={value} {...props} {...itemProps} />
    } else if (type === 'button') {
      // eslint-disable-next-line react/prop-types
      const { onHandleClick, ...restProps } = props
      template = (
        <Button
          key={`table-${tableId}_button-${rowIndex}-${cellIndex}-${itemIndex}`}
          label={value}
          onClick={(e) => {
            e.stopPropagation()
            if (onHandleClick) {
              necessaryFunctions[onHandleClick]({
                tableData,
                cell: item,
                cellItemIndex: itemIndex,
                cellIndex,
                row,
                rowIndex,
                groupIndex
              })
            }
          }}
          {...restProps}
          {...itemProps}
        />
      )
    } else if (type === 'icon') {
      template = <fil-icon key={`table-${tableId}_icon-${rowIndex}-${cellIndex}-${itemIndex}`} data-fillable {...props} {...itemProps} />
    } else if (type === 'dropdown') {
      template = (
        <Dropdown
          key={`table-${tableId}_dropdown-${rowIndex}-${cellIndex}-${itemIndex}`}
          id={`table-${tableId}_dropdown-${rowIndex}-${cellIndex}-${itemIndex}`}
          {...props}
          {...itemProps}
        />
      )
    } else if (type === 'customElement') {
      const CustomElement = necessaryElements[item.value]
      template = <CustomElement key={`table-${tableId}_${item.value}-${rowIndex}-${cellIndex}-${itemIndex}`} {...props} {...itemProps} />
    } else if (type === 'Pagination') {
      template = (
        <Pagination
          key={`table-${tableId}_pagination-${rowIndex}-${cellIndex}-${itemIndex}`}
          {...props}
          {...itemProps}
          {...{ totalItemsNum: tableData.body.length, defaultItemsCount: itemsCount, callback: handlePaginationChange }}
        />
      )
    } else {
      const finalValue = Array.isArray(value) ? value.map((valueItem, index) => <div key={index}>{valueItem}</div>) : value
      template = <span key={`table-${tableId}_default-${rowIndex}-${cellIndex}-${itemIndex}`}>{finalValue}</span>
    }

    return template
  }

  const getRowSelector = (row, rowIndex, groupIndex) => {
    return (
      <Checkbox
        checked={!!row.selected}
        id={`table-${tableId}_checkbox-${rowIndex}`}
        onChange={(status) => {
          handleRowSelection(groupIndex, rowIndex, status)
        }}
      />
    )
  }

  const handleToggle = (groupIndex) => {
    const copiedTableData = deepClone(tableData)
    copiedTableData.body[groupIndex].parentNode.expanded = !copiedTableData.body[groupIndex].parentNode.expanded

    let newAllExpandedStatus = false

    copiedTableData.body.forEach((item) => {
      newAllExpandedStatus = item.parentNode ? newAllExpandedStatus || item.parentNode.expanded : newAllExpandedStatus
    })

    expandAllGroup(newAllExpandedStatus)
    setTableData(copiedTableData)
  }

  const handleAllExpend = (e) => {
    e.stopPropagation()
    const newExpendedStatus = !isExpandedAllGroup
    const copiedTableData = deepClone(tableData)
    const body = copiedTableData.body.map((item) => {
      const newParentNode = item.parentNode
        ? {
            parentNode: {
              ...item.parentNode,
              expanded: newExpendedStatus
            }
          }
        : {}
      return {
        ...item,
        ...newParentNode
      }
    })

    expandAllGroup(newExpendedStatus)

    setTableData({
      ...copiedTableData,
      body
    })
  }

  const getHeaders = () => {
    let withColspabHeader = false
    let currentColspan = ''
    const colspanArray = []
    const normalTableHeader = []

    tableData.headers.forEach((item, index) => {
      // Create header extra colspan
      const tempColspan = item.colspan ? item.colspan : ''
      if (tempColspan !== currentColspan) {
        colspanArray.push([tempColspan])
        currentColspan = tempColspan
      } else if (colspanArray.length === 0) {
        colspanArray.push([tempColspan])
      } else {
        colspanArray[colspanArray.length - 1].push(tempColspan)
      }

      withColspabHeader = !_(colspanArray).flattenDeep().uniq().without('').isEmpty()

      // Create normal table header element
      normalTableHeader.push(
        <th
          key={`table_${tableId}_theader_${index}`}
          className={classnames({
            'fil-table-header--sortable': item.sortable,
            [`sorted-${item.sortedBy}`]: item.sortedBy,
            [item.extraClasses]: item.extraClasses,
            'fil-table-header--with-top-border': withColspabHeader && item.colspan
          })}
          style={{ ...(item.style || {}) }}
          onClick={(e) => {
            if (!e.target.className.includes('fil-checkbox')) {
              if (item.sortable) {
                if (item.handleSort) {
                  item.handleSort(index)
                } else {
                  const newSortedTableData = commonSortHandler({
                    sortedData: tableData,
                    index,
                    type: item.type,
                    sortedBy: item.sortedBy,
                    sortedItem: item.sortedItem,
                    isDefaultSort: false
                  })
                  setTableData(newSortedTableData)
                }
              }
            }
          }}
          aria-sort={item.sortable ? (item.ascSort ? 'ascending' : 'descending') : 'none'}
          aria-label={item.value}
          role="columnheader"
          colSpan={item.colspanSelf}
        >
          <span className="fil-table--selectall fil-table-row-selector">
            {index === 0 && isSelectable && (
              <Checkbox
                id={`${tableId}_select_all`}
                indeterminate={selectStatus === 'indeterminate'}
                checked={selectStatus === 'all'}
                onChange={handleSelectAllSelection}
              />
            )}
            {/* {index === 0 && <fil-icon icon="custom-chevron-down" size="12" />} */}
            {index !== 0 && item.sortable && (
              <span className="fil-table--sort">
                <fil-icon class="fil-table--sort-asc" size="16" icon="custom-arrow-up" />
                <fil-icon size="16" icon="custom-arrow-down" class="fil-table--sortable" />
              </span>
            )}
            {withOneKeyExpand && hasExpandedArrow && index === 0 && (
              <span className="fil-table--expended-icon" onClick={handleAllExpend}>
                <fil-icon size="16" icon={`custom-chevron-${isExpandedAllGroup ? 'up' : 'down'}`} />
              </span>
            )}
            <span id={`table_${tableId}_theader_content_${item.value}`} className="fil-table-header--content">
              {/* <FormattedMessage id={item.value} /> */}
              {getComponentTemplate({
                item: {
                  value: item.value,
                  type: item.type,
                  props: item.props
                }
              })}
            </span>
            {index === 0 && item.sortable && (
              <span className="fil-table--sort">
                <fil-icon class="fil-table--sort-asc" size="16" icon="custom-arrow-up" />
                <fil-icon size="16" icon="custom-arrow-down" class="fil-table--sortable" />
              </span>
            )}
          </span>
        </th>
      )
    })

    return (
      <React.Fragment>
        {withColspabHeader && (
          <tr className="fil-table-row--colspan">
            {colspanArray.map((item, itemIndex) => {
              return (
                <th
                  key={`table_${tableId}_colspan_${itemIndex}`}
                  className={classnames({
                    'fil-table-row--colspan--with-content': item[0]
                  })}
                  colSpan={item.length}
                >
                  {item[0] || ''}
                </th>
              )
            })}
          </tr>
        )}
        {
          <tr
            role="row"
            className={classnames({
              'fil-table-row--without-top-border': withColspabHeader
            })}
          >
            {normalTableHeader}
          </tr>
        }
      </React.Fragment>
    )
  }

  const getRows = () => {
    const getRow = ({ row, rowIndex, groupIndex, parentNode, trExtraClasses }) => (
      <tr
        key={`table_${tableId}_row_${rowIndex}`}
        role="row"
        className={classnames(trExtraClasses, {
          'fil-table-row--selected': !!row.selected,
          'fil-hide': !!parentNode && !parentNode.expanded
        })}
        aria-label="Row"
      >
        {row.cells.map((cell, cellIndex) => {
          const rowExtraClasses = row.extraClasses || ''
          const headerValue = !_.isEmpty(tableData.headers)
            ? tableData.headers[cellIndex]
              ? tableData.headers[cellIndex].value
              : tableData.headers[0].value
            : ''
          const formattedHeaderValue = Array.isArray(headerValue) ? headerValue.join('') : headerValue
          return (
            <td
              key={`table_${tableId}_cell_${cellIndex}`}
              role="cell"
              className={classnames(
                'fil-table-row--column',
                {
                  'fil-table-row--mobile-header': cellIndex === 0 && !isSimpleTable && !isScrollable,
                  'text-right': cellIndex !== 0 && !rowExtraClasses.includes('text-left')
                },
                rowExtraClasses
              )}
              data-heading={formattedHeaderValue}
              aria-label={cellIndex === 0 && isSelectable ? 'Row Selector' : 'Column'}
            >
              {cellIndex === 0 && isScrollable && sticky && (
                <div className="fil-table-row-selector--hover">{(Array.isArray(cell) ? cell : [cell]).map((j) => j.value)}</div>
              )}
              <div className="fil-table-row-selector">
                {cellIndex === 0 && isSelectable && getRowSelector(row, rowIndex, groupIndex)}
                <span>
                  {(Array.isArray(cell) ? cell : [cell]).map((cellItem, cellItemIndex) => {
                    return getComponentTemplate({ item: cellItem, itemIndex: cellItemIndex, cellIndex, row, rowIndex, groupIndex })
                  })}
                  {/* {index === 0 && item.type === 'input' ? `<span className="fil-table--error-text">${item.errortext}</span>` : null} */}
                </span>
              </div>
            </td>
          )
        })}
      </tr>
    )

    const splitedData = splitData(tableData.body)
    return !_.isEmpty(splitedData) ? (
      splitedData.map((group, groupIndex) => {
        const { parentNode, rows, trExtraClasses } = group

        const parentElement = () => {
          return (
            <tr
              role="row"
              className={classnames('fil-table-row--parent', {
                [`fil-table-row--${parentNode.expanded ? 'expanded' : 'collapsed'}`]: true
              })}
              aria-label="Section Heading"
              onClick={() => {
                handleToggle(groupIndex)
              }}
            >
              {!parentNode.separated && (
                <td role="gridcell" className="fil-table-row--mobile-header" colSpan={rows[0].cells.length}>
                  {parentNode.collapsible && (
                    <span className="fil-table-row--mobile-header-icon">
                      <fil-icon size="12" icon={`custom-chevron-${parentNode.expanded ? 'up' : 'down'}`} />
                    </span>
                  )}
                  <span className="fil-table-row--mobile-header-title">
                    {parentNode.cell.map((headerCell, headerCellIndex) => (
                      <span
                        key={`table_${tableId}_row_header_cell_${headerCellIndex}`}
                        className={`fil-table-row--mobile-header-title-cell ${headerCell.extraClasses}`}
                      >
                        {headerCell.value}
                      </span>
                    ))}
                  </span>
                </td>
              )}
              {parentNode.separated &&
                parentNode.cell.map((cell, cellIndex) => {
                  const headerValue = tableData.headers[cellIndex] ? tableData.headers[cellIndex].value : tableData.headers[0].value
                  const formattedHeaderValue = Array.isArray(headerValue) ? headerValue.join('') : headerValue
                  return (
                    <td
                      key={`table_${tableId}_cell_${cellIndex}`}
                      role="cell"
                      className={classnames(
                        'fil-table-row--column',
                        {
                          'fil-table-row--mobile-header': cellIndex === 0 && !isSimpleTable,
                          'text-right': cellIndex !== 0
                        },
                        parentNode.extraClasses
                      )}
                      data-heading={formattedHeaderValue}
                      aria-label={cellIndex === 0 && isSelectable ? 'Row Selector' : 'Column'}
                    >
                      {parentNode.collapsible && cellIndex === 0 && (
                        <span
                          className={classnames('fil-table-row--mobile-header-icon', {
                            'fil-table-row--mobile-header-icon-hidden': rows.length === 0
                          })}
                        >
                          <fil-icon size="12" icon={`custom-chevron-${parentNode.expanded ? 'up' : 'down'}`} />
                        </span>
                      )}
                      <span className="fil-table-row--mobile-header-title">
                        {getComponentTemplate({
                          item: cell
                        })}
                      </span>
                    </td>
                  )
                })}
            </tr>
          )
        }

        return (
          <React.Fragment key={`table_${tableId}_group_${groupIndex}`}>
            {parentNode ? (
              <React.Fragment>
                {parentElement()}
                {rows.map((row, rowIndex) => getRow({ row, rowIndex, groupIndex, parentNode }))}
              </React.Fragment>
            ) : (
              getRow({ row: group, rowIndex: groupIndex, trExtraClasses })
            )}
          </React.Fragment>
        )
      })
    ) : (
      <tr className="fil-table-row--without-data">
        <td colSpan="100">{withoutDataPlaceholder || intl.formatMessage({ id: 'noData' })}</td>
      </tr>
    )
  }

  const getMobileSortOptionsConf = (originTableHeader) => {
    const copiedTableHeader = deepClone(originTableHeader)
    const mobileSortOptionsIndexArray = []

    const getMobileSortOptionsIndex = (value) => {
      mobileSortOptionsIndexArray.push(value)
      return value.itemName
    }

    const mobileSortOptions = _(copiedTableHeader)
      .map((headerItem, headerItemIndex) => {
        const filterItemValue = {
          index: headerItemIndex,
          type: headerItem.type,
          sortedItem: headerItem.sortedItem
        }

        return headerItem.sortable
          ? [
              {
                label: intl.formatMessage({ id: 'fundList.mobileSortContent' }, { sortItem: headerItem.value }),
                value: getMobileSortOptionsIndex({
                  ...filterItemValue,
                  itemName: headerItem.value
                }),
                textLabel: intl.formatMessage({ id: 'fundList.mobileSortContent' }, { sortItem: headerItem.value })
              }
            ]
          : []
      })
      .filter((headerItem) => !_.isEmpty(headerItem))
      .flattenDeep()
      .value()

    return {
      mobileSortOptions,
      mobileSortOptionsIndexes: mobileSortOptionsIndexArray
    }
  }

  useEffect(() => {
    if (isSelectable) {
      setSelectStatus(getTopSelectorStatus(defaultTableData))
    }

    const firstSortedItemIndex = _(defaultTableData.headers)
      .map((headerItem, headerItemIndex) => {
        if (headerItem.sortedBy) {
          return headerItemIndex
        }

        return ''
      })
      .without('')
      .head()

    const defaultSortedItemIndex = _.isUndefined(sortIndex)
      ? !!firstSortedItemIndex || firstSortedItemIndex === 0
        ? firstSortedItemIndex
        : 0
      : sortIndex

    if (!_.isEmpty(defaultTableData.headers)) {
      const { type, sortedItem, sortedBy } = defaultTableData.headers[defaultSortedItemIndex]
      const firstSortedData =
        defaultTableData.body && needInitSort
          ? commonSortHandler({
              sortedData: defaultTableData,
              index: defaultSortedItemIndex,
              type,
              sortedBy: sortByStatusRef.current || sortedBy,
              sortedItem,
              isDefaultSort: !sortByStatusRef.current,
              firstSort: true
            })
          : defaultTableData
      const itemsPerCount = !_.isEmpty(firstSortedData.pagination)
        ? firstSortedData.pagination.defaultItemsCount
        : firstSortedData.body
          ? firstSortedData.body.length
          : 0

      if (defaultTableData.body) {
        let withAllExpandedRow = false
        let allExpandedRowStatus = false
        defaultTableData.body.forEach((item) => {
          if (!withAllExpandedRow) {
            withAllExpandedRow = !!item.parentNode && !!item.rows.length
          }

          if (item.parentNode) {
            allExpandedRowStatus = allExpandedRowStatus || item.parentNode.expanded
          }
        })

        setHasExpandedArrow(withAllExpandedRow)
        expandAllGroup(allExpandedRowStatus)

        setMobileSortOptionsConf(getMobileSortOptionsConf(defaultTableData.headers))
      }

      setItemsCount(itemsPerCount)
      setTableData(firstSortedData)
    } else {
      setItemsCount(defaultTableData.body.length)
      setTableData(defaultTableData)
    }
  }, [defaultTableData])

  useEffect(() => {
    if (!scrollable && sticky) {
      setScrollableStatus(true)
    }
  }, [])

  const handleMobileSortChange = ({ sortOpt = mobileSortOpt, sortBy }) => {
    const { mobileSortOptionsIndexes } = mobileSortOptionsConf
    const [selectMobileSortOpt] = mobileSortOptionsIndexes.filter((opt) => opt.itemName === sortOpt)
    const mobileSortedTableData = commonSortHandler({
      sortedData: tableData,
      index: selectMobileSortOpt.index,
      type: selectMobileSortOpt.type,
      sortedBy: sortBy,
      sortedItem: selectMobileSortOpt.sortedItem,
      keepCurrentSortStatus: !!sortBy
    })
    setTableData(mobileSortedTableData)
  }

  return (
    <React.Fragment>
      {tableData && (
        <div
          className={classnames(
            'fil-table-wrapper small-12 fil-fund-table__body',
            {
              'fil-table-simple-table': isSimpleTable,
              'fil-table-wrapper--scrollable': isScrollable,
              'fil-table-wrapper--sticky-column': isScrollable && sticky && !isIEbrowser,
              // todo: IE11
              'fil-table-wrapper--sticky-colum-ie': isScrollable && sticky && isIEbrowser,
              'fil-table-headless-column': _.isEmpty(tableData.headers)
            },
            extraClasses
          )}
        >
          {/* Mobile Filter */}
          {!isScrollable && !isSimpleTable && hasMobileSort && tableData.body && (
            <div className="fil-table--sort-menu">
              <Grid>
                <Cell extraClasses="small-8 medium-9">
                  <Select
                    className="fil-table--sort-select"
                    onChange={(e) => {
                      handleMobileSortChange({ sortBy: '', sortOpt: e.value })
                    }}
                    value={mobileSortOpt}
                    options={JSON.stringify(mobileSortOptionsConf.mobileSortOptions)}
                  />
                </Cell>
                <Cell extraClasses="small-4 medium-3 text-right">
                  <Switch
                    id="fil-table--mobile-switch"
                    items={mobileSortSwitchContent}
                    defaultSelectedId={sortByStatusRef.current}
                    onChange={(e) => {
                      handleMobileSortChange({ sortBy: e.item.value })
                    }}
                  />
                </Cell>
              </Grid>
            </div>
          )}
          {title && (
            <div id={`${tableId}__table-title`} className="text-heading-s fil-table--title text-left">
              {title}
            </div>
          )}

          {/* ${this.scrollable && this.sticky && this.isIEbrowser
        ? html`<div class="table--scroller">
            <table class="${ifDefined(classMap(tableClasses))}" role="grid">
              ${this.caption
                ? html`<caption>
                    ${this.caption}
                  </caption>`
                : null}
              ${this.getColgroup()}
              <thead>
                ${this.getHeaders()}
              </thead>
              <tbody class="${ifDefined(classMap(tableBodyClasses))}">
                ${this.getRows(this.paginate(this.rows, this.itemsPerPage, this.pageNumber))}
              </tbody>
            </table>
          </div>` */}

          <table
            id={tableId}
            className={classnames('fil-table', {
              [`fil-table--${size}`]: size,
              // TODO
              'fil-table-body--small': textSize,
              'fil-table--responsive': !isScrollable,
              'fil-table--fixed': sticky
            })}
            role="grid"
            {...extraProps}
          >
            {!!caption && <caption>{caption}</caption>}
            {/* ${this.scrollable || this.sticky ? this.getColgroup() : null} */}
            {!_.isEmpty(tableData.headers) && <thead>{getHeaders()}</thead>}
            {tableData.body ? (
              <tbody
                className={classnames({
                  'fil-table-body--sections': tableData.body.filter((bodyItem) => bodyItem.parentNode).length
                })}
              >
                {getRows()}
              </tbody>
            ) : (
              <tbody>
                <tr className="fil-table-row--without-data">
                  <td colSpan="100">
                    <Loader />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          {!_.isEmpty(tableData.pagination) && (
            <div className="fil-table--footer">{getComponentTemplate({ item: tableData.pagination })}</div>
          )}
          {tableDisclaimer && (
            <div id={`${tableId}__table-disclaimer`} className="table-notes text-body-xs">
              {typeof tableDisclaimer === 'string' ? <span dangerouslySetInnerHTML={{ __html: tableDisclaimer }} /> : tableDisclaimer}
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  )
}

Table.displayName = 'Table'

Table.propTypes = {
  tableId: PropTypes.string.isRequired,
  tableData: PropTypes.object.isRequired,
  tableDisclaimer: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  necessaryElements: PropTypes.object,
  necessaryFunctions: PropTypes.object,
  isSelectable: PropTypes.bool,
  caption: PropTypes.string,
  size: PropTypes.string,
  textSize: PropTypes.string,
  pagination: PropTypes.object,
  extraClasses: PropTypes.string,
  needInitSort: PropTypes.bool,
  isSimpleTable: PropTypes.bool,
  hasMobileSort: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  withoutDataPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  defaultSortingValue: PropTypes.number,
  scrollable: PropTypes.bool,
  sticky: PropTypes.bool,
  withOneKeyExpand: PropTypes.bool,
  callback: PropTypes.func
}

Table.defaultProps = {
  tableDisclaimer: '',
  necessaryElements: {},
  necessaryFunctions: {},
  isSelectable: false,
  caption: '',
  size: 'standard',
  textSize: '',
  pagination: {},
  extraClasses: '',
  needInitSort: true,
  isSimpleTable: false,
  hasMobileSort: true,
  title: '',
  withoutDataPlaceholder: null,
  defaultSortingValue: 0,
  scrollable: false,
  sticky: false,
  withOneKeyExpand: true,
  callback: () => {}
}

export default Table
